import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, Trash2 } from "lucide-react";
import {
    deleteCompany,
    getCompany,
    updateCompany,
    type Company,
    type Contact,
    type NextAction,
    type OfficeCount,
    type Status,
    type Style,
} from "@/components/api/companiesApi";
import {
    OFFICE_COUNT_OPTIONS,
    STATUS_ORDER,
    normalizeHomepage,
    officeCountLabel,
} from "@/lib/cms";
import { formatEuroDate } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { StatusBadge } from "./components/StatusBadge";
import { NextActionEditor } from "./components/NextActionEditor";
import { ContactsSection } from "./components/ContactsSection";
import { StyleSection } from "./components/StyleSection";

type LoadState = "loading" | "ready" | "notfound";

export function CmsDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [company, setCompany] = useState<Company | null>(null);
    const [state, setState] = useState<LoadState>("loading");

    // Reset to the loading state when the route id changes — done during render
    // (rather than as a state-syncing effect) so the fetch effect below only
    // ever calls setState from its async callbacks.
    const [requestedId, setRequestedId] = useState(id);
    if (requestedId !== id) {
        setRequestedId(id);
        setState("loading");
        setCompany(null);
    }

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const data = await getCompany(id!);
                if (active) {
                    setCompany(data);
                    setState("ready");
                }
            } catch {
                if (active) setState("notfound");
            }
        })();
        return () => {
            active = false;
        };
    }, [id]);

    const save = async (patch: Partial<Company>) => {
        if (!company) return;
        try {
            const updated = await updateCompany(company.id, patch);
            setCompany(updated);
        } catch {
            // Error already surfaced via errorEvents by the API layer.
        }
    };

    if (state === "loading") {
        return <p className="p-6 text-muted-foreground">Loading…</p>;
    }

    if (state === "notfound" || !company) {
        return (
            <div className="p-6 max-w-2xl mx-auto">
                <div className="glass-card flex flex-col items-center text-center gap-3 py-16 px-6">
                    <h1 className="text-xl font-semibold">Company not found</h1>
                    <p className="text-sm text-muted-foreground">
                        This company may have been deleted, or the link is out of date.
                    </p>
                    <Button asChild>
                        <Link to="/cms">Back to Customers</Link>
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-3xl mx-auto space-y-6 animate-fade-in">
            <Link
                to="/cms"
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            >
                <ArrowLeft className="h-4 w-4" />
                Back to Customers
            </Link>

            <HeaderCard company={company} onSave={save} />

            <section className="glass-card p-5 space-y-2">
                <h2 className="font-semibold">Next action</h2>
                <NextActionEditor
                    nextAction={company.nextAction}
                    onChange={(next: NextAction | null) => save({ nextAction: next })}
                />
            </section>

            <DetailsCard company={company} onSave={save} />

            <ContactsSection
                contacts={company.contacts}
                onChange={(contacts: Contact[]) => save({ contacts })}
            />

            <StyleSection style={company.style} onChange={(style: Style) => save({ style })} />

            <DangerZone
                companyName={company.name}
                onDelete={async () => {
                    await deleteCompany(company.id);
                    navigate("/cms");
                }}
            />
        </div>
    );
}

function HeaderCard({
    company,
    onSave,
}: {
    company: Company;
    onSave: (patch: Partial<Company>) => void;
}) {
    const [name, setName] = useState(company.name);

    const commitName = () => {
        const trimmed = name.trim();
        if (!trimmed) {
            setName(company.name); // never allow an empty name
            return;
        }
        if (trimmed !== company.name) {
            setName(trimmed);
            onSave({ name: trimmed });
        }
    };

    return (
        <section className="glass-card p-5 space-y-4">
            <div className="flex items-center gap-3">
                <StatusBadge status={company.status} />
                <span className="text-xs text-muted-foreground">
                    Updated {formatEuroDate(company.updatedAt)}
                </span>
            </div>

            <div className="space-y-1.5">
                <Label htmlFor="company-name">Company name</Label>
                <Input
                    id="company-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onBlur={commitName}
                    className="text-lg font-semibold"
                />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                    <Label htmlFor="company-status">Status</Label>
                    <Select
                        value={company.status}
                        onValueChange={(v) => onSave({ status: v as Status })}
                    >
                        <SelectTrigger id="company-status">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {STATUS_ORDER.map((status) => (
                                <SelectItem key={status} value={status}>
                                    {status}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="company-lastcontacted">Last contacted</Label>
                    <DatePicker
                        id="company-lastcontacted"
                        value={company.lastContacted}
                        onChange={(v) => onSave({ lastContacted: v })}
                        clearable
                    />
                </div>
            </div>
        </section>
    );
}

function DetailsCard({
    company,
    onSave,
}: {
    company: Company;
    onSave: (patch: Partial<Company>) => void;
}) {
    const [description, setDescription] = useState(company.description);
    const [homepage, setHomepage] = useState(company.homepage ?? "");

    const commitHomepage = () => {
        const normalized = normalizeHomepage(homepage);
        setHomepage(normalized);
        const next = normalized || null;
        if (next !== company.homepage) onSave({ homepage: next });
    };

    return (
        <section className="glass-card p-5 space-y-4">
            <h2 className="font-semibold">Details</h2>

            <div className="space-y-1.5">
                <Label htmlFor="company-description">Description</Label>
                <Textarea
                    id="company-description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    onBlur={() => {
                        if (description !== company.description) onSave({ description });
                    }}
                    placeholder="Free-text notes about this company…"
                    rows={3}
                />
            </div>

            <div className="space-y-1.5">
                <Label htmlFor="company-offices">Number of offices</Label>
                <Select
                    value={company.officeCount == null ? "none" : String(company.officeCount)}
                    onValueChange={(v) =>
                        onSave({
                            officeCount: v === "none" ? null : (Number(v) as OfficeCount),
                        })
                    }
                >
                    <SelectTrigger id="company-offices" className="w-[180px]">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="none">Not set</SelectItem>
                        {OFFICE_COUNT_OPTIONS.map((count) => (
                            <SelectItem key={count} value={String(count)}>
                                {officeCountLabel(count)}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="space-y-1.5">
                <Label htmlFor="company-homepage">Homepage</Label>
                <div className="flex items-center gap-2">
                    <Input
                        id="company-homepage"
                        value={homepage}
                        onChange={(e) => setHomepage(e.target.value)}
                        onBlur={commitHomepage}
                        placeholder="acme.se"
                    />
                    {company.homepage && (
                        <a
                            href={company.homepage}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-primary hover:underline whitespace-nowrap"
                        >
                            Visit
                            <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                    )}
                </div>
            </div>
        </section>
    );
}

function DangerZone({
    companyName,
    onDelete,
}: {
    companyName: string;
    onDelete: () => void | Promise<void>;
}) {
    return (
        <section className="glass-card p-5 border-destructive/40">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="font-semibold">Delete company</h2>
                    <p className="text-sm text-muted-foreground">
                        For genuine mis-entries only. To close out a real lead, set the status to
                        Won or Lost instead.
                    </p>
                </div>
                <AlertDialog>
                    <AlertDialogTrigger asChild>
                        <Button variant="destructive" className="gap-1.5">
                            <Trash2 className="h-4 w-4" />
                            Delete
                        </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Delete {companyName}?</AlertDialogTitle>
                            <AlertDialogDescription>
                                This permanently removes the company and all its contacts. This
                                cannot be undone.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={onDelete}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                                Delete company
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>
        </section>
    );
}
